import { CheckboxControl, SelectControl, Loader } from '@liveconnect/components'
import { FC, useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'

import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import useAuth, { postLogoutRedirectPathKey } from '../../core/auth/useAuth'
import useLocalizations from '../../core/Localizations/useLocalizations'
import useMember from '../../core/member/useMember'
import usePrivacyPolicies from '../../core/privacyPolicies/usePrivacyPolicies'
import useUi from '../../core/ui/useUi'
import { DEFAULT_LANG } from '../../i18n/config'
import useSnackBars from '../../utils/notifications/useNotifications'

import './styles.scss'

interface Option {
  value: string
  label: string
}

const PrivacyPoliciesModal: FC = () => {
  const { getLocalizations } = useLocalizations()
  const { tenantId } = useParams()
  const { t } = useTranslation()
  const notify = useSnackBars()
  const { member, visibleMember } = useMember()
  const { logout } = useAuth()
  const { showConfirmation } = useUi()

  const [localizations, setLocalizations] = useState<Option[]>()
  const [isoCode, setIsoCode] = useState<string>(
    member && member.isoCode ? member.isoCode : DEFAULT_LANG
  )

  const [isAccepted, setIsAccepted] = useState<boolean>(false)

  const {
    isVisible,
    isReadOnly,
    privacyPolicies,
    isSigned,
    isLoaded,
    showModal,
    hideModal,
    setReadOnly,
    fetchPrivacyPolicies,
    signPrivacyPolicies,
  } = usePrivacyPolicies()

  const handleShow = () => {
    showModal()
  }

  const handleHide = () => {
    hideModal()
  }

  const handleLogout = () => {
    sessionStorage.setItem(postLogoutRedirectPathKey, `/t/${tenantId}`)
    logout()
  }

  const handleCancel = () => {
    handleHide()
    showConfirmation({
      title: t('common.warning'),
      subtitle: t('privacyPoliciesModal.cancelModal.text'),
      iconName: 'report_problem',
      onConfirm: () => handleShow(),
      onCancel: () => handleLogout(),
    })
  }

  const visibleUser = async (memberId: string) => {
    try {
      await visibleMember(memberId)
    } catch (e) {
      notify.error(t('common.unknownError'))
    }
  }

  const handleConfirm = async () => {
    try {
      await signPrivacyPolicies()
      member && member.id && (await visibleUser(member.id))
    } catch (e) {
      notify.error(t('common.unknownError'))
    }
    handleHide()
  }

  const handleChangeLocalization = (value: string) => {
    setIsoCode(value)
  }

  const fetchLocalizations = async () => {
    const localizations = await getLocalizations()
    setLocalizations(
      (localizations ?? []).map((item) => ({
        value: item.isoCode,
        label: item.name,
      }))
    )
  }

  const getPrivacyPolicies = () => {
    if (member && privacyPolicies != null) {
      const policyIsoCode = privacyPolicies?.find(
        (policy) => policy.isoCode == isoCode
      )
      if (policyIsoCode) return policyIsoCode.value
    }

    return ''
  }

  useEffect(() => {
    if (isVisible) {
      fetchLocalizations()
      if (privacyPolicies == null) fetchPrivacyPolicies()
    }
  }, [isVisible])

  useEffect(() => {
    if (isLoaded && !isSigned) {
      setReadOnly(false)
      handleShow()
    } else {
      setReadOnly(true)
    }
  }, [isSigned, isLoaded])

  return (
    <Modal
      show={isVisible}
      size="lg"
      onHide={handleHide}
      className="PrivacyPoliciesModal"
      keyboard={isReadOnly}
      backdrop={isReadOnly ? undefined : 'static'}
      backdropClassName="PrivacyPoliciesModalBackdrop"
    >
      <Modal.Header>
        <Modal.Title>{t('privacyPoliciesModal.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {localizations && (
          <div className="d-flex justify-content-end">
            <SelectControl
              className="no-borders"
              name="isoCode"
              value={isoCode}
              onChange={handleChangeLocalization}
              label=""
              hiddenLabel
              noOptionsMessage=""
              options={localizations}
              isClearable={false}
            />
          </div>
        )}

        <div className="scrollable-content">
          {privacyPolicies ? (
            <div
              dangerouslySetInnerHTML={{ __html: getPrivacyPolicies() }}
            ></div>
          ) : (
            <Loader />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="privacy-policies-modal__footer">
        {!isReadOnly && (
          <CheckboxControl
            label={t('privacyPoliciesModal.checkbox')}
            name={'accept'}
            value={isAccepted}
            onChange={(newValue) => setIsAccepted(newValue)}
          />
        )}
        <div className="privacy-policies-modal__footer-actions">
          {isReadOnly && (
            <button className="btn btn-primary flex-none" onClick={handleHide}>
              {t('common.close')}
            </button>
          )}
          {!isReadOnly && (
            <>
              <button
                className="btn btn-outline-primary"
                onClick={handleCancel}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirm}
                disabled={!isAccepted}
              >
                {t('common.accept')}
              </button>
            </>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default PrivacyPoliciesModal
